/**
 * NearLossDirector (S195 / BL-283, audit beat #9) — ONE owner for the
 * near-loss state, polled per render frame (the ComboReadout precedent:
 * render-only reads of live state; never a sim mutation, never a new event).
 *
 * The audit's findings it retires:
 *   • the ≤3-lives band was BINARY — 3, 2 and 1 lives felt identical; the
 *     bands now escalate (tint base 0.20/0.26/0.32 · heartbeat 800/650/520 ms
 *     · music pressure ⅓/⅔/1 — all from the feelBudget canon);
 *   • the MusicDirector was lives-blind — `setPressure` is its new composable
 *     overlay (the interlude pattern);
 *   • NOTHING disarmed on recovery (S195-F14): `addLives` mutates silently,
 *     so the tint latched to run-end and the heartbeat kept beating until the
 *     next hit. Polling reads the truth every frame — the frame a rescue
 *     lifts lives above 3, everything releases and the S194 comeback beat
 *     finally lands on a calm screen.
 *
 * Photosensitivity by construction: the tint pulse stays 0.8 Hz with a ±0.12
 * swing on a base ≤0.32 (edge bands only, never full-screen) — ScreenEffects
 * clamps the bound; reduced motion keeps the steady tint. The heartbeat +
 * pressure are audio (information lands for PRM users by design).
 */
import {
  NEAR_LOSS_PRESSURE,
  NEAR_LOSS_TINT_BASE,
  nearLossBand,
} from "../core/feelBudget.ts";

/** ScreenEffects slice (DI for spies — the CombatFeel pattern). */
export interface NearLossScreenEffects {
  setIntegrityDanger: (active: boolean, baseAlpha?: number) => void;
}

/** AudioEngine slice — band-cadenced heartbeat (0 stops it). */
export interface NearLossAudio {
  setNearLossLevel: (band: 0 | 1 | 2 | 3) => void;
}

/** MusicDirector slice — the pressure overlay (0 releases it). */
export interface NearLossMusic {
  setPressure: (value: number) => void;
}

export interface NearLossDirectorDeps {
  getLives: () => number;
  screenEffects: NearLossScreenEffects;
  audio?: NearLossAudio | null;
  music?: NearLossMusic | null;
}

export class NearLossDirector {
  private readonly deps: NearLossDirectorDeps;
  private band: 0 | 1 | 2 | 3 = 0;

  constructor(deps: NearLossDirectorDeps) {
    this.deps = deps;
  }

  /** The active band — exposed for tests/telemetry. */
  get currentBand(): 0 | 1 | 2 | 3 {
    return this.band;
  }

  /** Poll once per render frame. O(1); only band EDGES touch the channels. */
  update(): void {
    const band = nearLossBand(this.deps.getLives());
    if (band === this.band) return;
    this.band = band;
    this.deps.screenEffects.setIntegrityDanger(
      band > 0,
      NEAR_LOSS_TINT_BASE[band],
    );
    this.deps.audio?.setNearLossLevel(band);
    this.deps.music?.setPressure(NEAR_LOSS_PRESSURE[band]);
  }

  /** Hard release (run end / menu) — everything back to calm, re-armed. */
  reset(): void {
    if (this.band === 0) return;
    this.band = 0;
    this.deps.screenEffects.setIntegrityDanger(false, NEAR_LOSS_TINT_BASE[0]);
    this.deps.audio?.setNearLossLevel(0);
    this.deps.music?.setPressure(0);
  }
}
